'use client';

import React, { useState, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import { Box } from '@/components/atoms/Box';
import { Img } from '@/components/atoms/Img';
import { Video } from '@/components/atoms/Video';

interface BackgroundVideoProps {
    videoUrl: string;
    posterUrl: string;
    /** Responsive srcset for poster image */
    posterSrcSet?: string;
    /** Sizes attribute for responsive poster */
    posterSizes?: string;
    priority?: boolean;
    className?: string;
    width?: number;
    height?: number;
}

/**
 * Optimized background video component that:
 * 1. Shows a static poster image immediately (becomes LCP)
 * 2. Defers video loading until after page load to not block LCP
 * 3. Crossfades to video once it's ready to play
 *
 * This prevents large videos from blocking LCP.
 */
export const BackgroundVideo = ({
    videoUrl,
    posterUrl,
    posterSrcSet,
    posterSizes,
    priority = false,
    className,
    width,
    height,
}: BackgroundVideoProps) => {
    const [isVideoReady, setIsVideoReady] = useState(false);
    const [shouldLoadVideo, setShouldLoadVideo] = useState(false);
    const videoRef = useRef<HTMLVideoElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // Defer video loading until after page load completes
    // This ensures the poster image is the LCP element, not the video
    useEffect(() => {
        // Use requestIdleCallback to load video when browser is idle
        // Falls back to setTimeout for browsers without support
        const loadVideo = () => {
            if ('requestIdleCallback' in window) {
                (window as Window & { requestIdleCallback: (cb: () => void) => void })
                    .requestIdleCallback(() => setShouldLoadVideo(true));
            } else {
                // Delay video loading by 2 seconds to ensure LCP is measured with poster
                setTimeout(() => setShouldLoadVideo(true), 2000);
            }
        };

        // Wait for page load before starting video load
        if (document.readyState === 'complete') {
            loadVideo();
        } else {
            window.addEventListener('load', loadVideo, { once: true });
            return () => window.removeEventListener('load', loadVideo);
        }
    }, []);

    // Handle video ready state
    const handleCanPlayThrough = () => {
        setIsVideoReady(true);
    };

    return (
        <Box ref={containerRef} className={cn("absolute inset-0 overflow-hidden", className)}>
            {/* Poster image - always visible initially, becomes LCP */}
            <Img
                src={posterUrl}
                srcSet={posterSrcSet}
                sizes={posterSizes}
                alt="Background video poster"
                aria-hidden="true"
                loading={priority ? "eager" : "lazy"}
                decoding={priority ? "sync" : "async"}
                fetchPriority={priority ? "high" : undefined}
                className={cn(
                    "absolute inset-0 size-full object-cover transition-opacity duration-700",
                    isVideoReady ? "opacity-0" : "opacity-100"
                )}
                width={width}
                height={height}
            />

            {/* Video - loads after page load, crossfades in when ready */}
            {shouldLoadVideo && (
                <Video
                    ref={videoRef}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="auto"
                    onCanPlayThrough={handleCanPlayThrough}
                    className={cn(
                        "absolute inset-0 size-full object-cover transition-opacity duration-700",
                        isVideoReady ? "opacity-100" : "opacity-0"
                    )}
                    width={width}
                    height={height}
                >
                    <source src={videoUrl} type="video/mp4" />
                </Video>
            )}
        </Box>
    );
};
